import { Box, Dialog, DialogActions, DialogContent, DialogTitle, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { UI, btnSx } from "../components/ActionDrawer";
import DashTable, { FilterChips, PageHead, Tag } from "../components/DashTable";
import PageLoader from "../components/Loader";
import { useToast } from "../components/Toast";
import apiService from "../Services/api.service";

const PAGE_SIZE = 15;

const STATUS_META = {
  new: { label: "New", tone: "s" },
  order_created: { label: "Order created", tone: "g" },
  dismissed: { label: "Dismissed", tone: "i" },
};

const rupee = (n) => (n ? `₹${Number(n).toLocaleString("en-IN", { maximumFractionDigits: 0 })}` : "—");

const dateTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
};

const doctorName = (deal) =>
  [deal.parsed?.salutation, deal.parsed?.firstName, deal.parsed?.lastName].filter(Boolean).join(" ") || "—";

const planLabel = (parsed) => {
  if (!parsed?.engagePlan) return parsed?.planText || "—";
  if (parsed.engagePlan === "base model") return "Base model";
  const name = parsed.engagePlan[0].toUpperCase() + parsed.engagePlan.slice(1);
  return parsed.tenureMonths ? `${name}, ${parsed.tenureMonths} months` : name;
};

/* "Deal Closed" posts from the sales Slack channel. The message pre-fills the New Order form; whoever
   creates the order fills in what the message did not say. */
const SlackDeals = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const token = localStorage.getItem("token");

  const [filter, setFilter] = useState("new");
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ data: [], summary: null, pagination: null });
  const [isLoading, setIsLoading] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [selected, setSelected] = useState(null);
  const [dismissing, setDismissing] = useState(false);

  const fetchRows = useCallback(async () => {
    setIsLoading(true);
    setLoadFailed(false);
    try {
      setData(await apiService.fetchSlackDeals(token, { status: filter, page, limit: PAGE_SIZE }));
    } catch (error) {
      setLoadFailed(true);
      toast.error("Error while fetching Slack deals");
    } finally {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, filter, page]);

  useEffect(() => {
    setPage(1);
  }, [filter]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const createOrder = (deal) => navigate("/dashboard/orders/add-order", { state: { slackDeal: deal } });

  const handleDismiss = async (deal) => {
    setDismissing(true);
    try {
      await apiService.dismissSlackDeal(deal._id, token);
      toast.success("Deal dismissed");
      setSelected(null);
      fetchRows();
    } catch (error) {
      toast.error(error.response?.data?.message || "Could not dismiss this deal");
    } finally {
      setDismissing(false);
    }
  };

  const summary = data.summary;
  const pagination = data.pagination;
  const chips = [
    { value: "new", label: `New (${summary?.new ?? 0})` },
    { value: "order_created", label: `Order created (${summary?.order_created ?? 0})` },
    { value: "dismissed", label: `Dismissed (${summary?.dismissed ?? 0})` },
  ];
  const kpis = summary ? [{ label: "Waiting for an order", value: summary.new, alarm: summary.new > 0 }] : [];

  return (
    <Box sx={{ padding: { xs: "18px 16px 50px", md: "24px 28px 60px" }, maxWidth: 1240 }}>
      <PageHead
        title="Slack Deals"
        lede="Order requests from Slack: the /new-order form (every field filled) and Deal Closed posts (only what the message said). Create order opens the New Order form already filled in. Check it, set the target amount, and create the order."
        kpis={kpis}
      />

      <FilterChips options={chips} value={filter} onChange={setFilter} />

      <DashTable
        columns={[
          { label: "Posted" },
          { label: "From" },
          { label: "Doctor" },
          { label: "Clinic" },
          { label: "Plan" },
          { label: "Deal size", num: true },
          { label: "Status" },
          { label: "Action" },
        ]}
        rows={(data.data || []).map((deal) => ({
          id: deal._id,
          cells: [
            dateTime(deal.createdAt),
            <Box component="span">
              <Tag tone={deal.source === "form" ? "g" : "i"}>{deal.source === "form" ? "Form" : "Message"}</Tag>
              {deal.submittedByName ? (
                <Box component="span" sx={{ display: "block", fontSize: 11, color: UI.ink3, marginTop: "2px" }}>
                  {deal.submittedByName}
                </Box>
              ) : null}
            </Box>,
            doctorName(deal),
            deal.parsed?.clinicName || "—",
            planLabel(deal.parsed),
            rupee(deal.parsed?.dealAmount),
            <Tag tone={STATUS_META[deal.status]?.tone || "i"}>
              {deal.status === "order_created" && deal.orderNumber
                ? deal.orderNumber
                : STATUS_META[deal.status]?.label || deal.status}
            </Tag>,
            deal.status === "new" ? (
              <Box
                component="button"
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  createOrder(deal);
                }}
                sx={btnSx("primary")}
              >
                Create order
              </Box>
            ) : deal.status === "order_created" && deal.orderId ? (
              <Box
                component="button"
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/dashboard/orders/${deal.orderId}`);
                }}
                sx={btnSx("ghost")}
              >
                Open order
              </Box>
            ) : (
              "—"
            ),
          ],
        }))}
        onRowClick={(id) => setSelected((data.data || []).find((d) => d._id === id) || null)}
        emptyText={filter === "new" ? "No Slack requests waiting. New /new-order forms and Deal Closed posts show up here." : "Nothing here."}
        errorText={loadFailed ? "Could not load Slack deals." : null}
        onRetry={fetchRows}
        foot={`${(data.data || []).length} shown · ${pagination?.total || 0} total`}
      />

      {pagination && pagination.total > 0 && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: "10px",
            marginBottom: "10px",
            fontSize: 13,
            color: UI.ink3,
          }}
        >
          <span>
            Page {pagination.page} of {pagination.totalPages}
          </span>
          <Box sx={{ display: "flex", gap: "8px" }}>
            <Box
              component="button"
              type="button"
              onClick={() => setPage((p) => Math.max(p - 1, 1))}
              disabled={pagination.page <= 1}
              sx={btnSx("ghost", pagination.page <= 1)}
            >
              Previous
            </Box>
            <Box
              component="button"
              type="button"
              onClick={() => setPage((p) => Math.min(p + 1, pagination.totalPages))}
              disabled={pagination.page >= pagination.totalPages}
              sx={btnSx("ghost", pagination.page >= pagination.totalPages)}
            >
              Next
            </Box>
          </Box>
        </Box>
      )}

      <Dialog open={Boolean(selected)} onClose={() => setSelected(null)} fullWidth maxWidth="sm">
        {selected && (
          <>
            <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Box component="span" sx={{ fontSize: 18 }}>
                {selected.parsed?.clinicName || "Slack deal"}
              </Box>
              <IconButton onClick={() => setSelected(null)} size="small" aria-label="Close">
                <CloseIcon fontSize="small" />
              </IconButton>
            </DialogTitle>
            <DialogContent dividers>
              {selected.missing?.length ? (
                <Box sx={{ fontSize: 12, color: UI.amber, marginBottom: "12px" }}>
                  Not in the message: {selected.missing.join(", ")}
                </Box>
              ) : null}
              <Box sx={{ fontSize: 11, color: UI.ink3, marginBottom: "6px" }}>
                Original message{selected.editedAt ? " (edited)" : ""}
              </Box>
              <Box
                sx={{
                  whiteSpace: "pre-wrap",
                  overflowWrap: "anywhere",
                  fontSize: 13,
                  bgcolor: UI.bg2,
                  borderRadius: "4px",
                  padding: "10px 12px",
                }}
              >
                {selected.text}
              </Box>
            </DialogContent>
            {selected.status === "new" && (
              <DialogActions sx={{ padding: "12px 24px", gap: "8px" }}>
                <Box
                  component="button"
                  type="button"
                  onClick={() => handleDismiss(selected)}
                  disabled={dismissing}
                  sx={btnSx("ghost", dismissing)}
                >
                  {dismissing ? "Dismissing…" : "Dismiss"}
                </Box>
                <Box component="button" type="button" onClick={() => createOrder(selected)} sx={btnSx("primary")}>
                  Create order
                </Box>
              </DialogActions>
            )}
          </>
        )}
      </Dialog>

      {isLoading && <PageLoader />}
    </Box>
  );
};

export default SlackDeals;
